import { Box, Typography } from "@mui/material";
import { findNearestCandidate } from "components/identifyEventsTool/utils/findNearestCandidate";
import Plot from "components/plotly/LocalisedPlot";
import { CENTRED_PLAYHEAD_MARKER_SHAPE } from "components/plotly/playheadMarker";
import { eventTypeColor } from "components/scrapEyeDataDashboard/VideoPlayer/utils";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import dayjs, { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import type { Annotations, Data, Layout, Shape } from "plotly.js";
import { useMemo } from "react";
import { EventCreate, ScaleReadingLookup } from "src/store/api/generatedApi";
import {
  computeLinearDtick,
  computeVisibleYRange,
  findWeightAt,
  getAreaColor,
  getCandidateLabels,
  parseFiniteFloat,
  ScaleWeightSeriesData,
} from "./ScaleWeightsChart.utils";
import { useWindowedSeries } from "./useWindowedSeries";

const toPlotlyUtcString = (date: Date): string =>
  date.toISOString().slice(0, 19);

interface ScaleWeightsSingleAreaChartProps {
  scrapCar: string;
  areaIndex: number;
  seriesData: ScaleWeightSeriesData | undefined;
  events: ScrapEyesEvent[];
  candidateEvents: EventCreate[];
  massAreaMatching: boolean | undefined;
  scaleReadingLookup: ScaleReadingLookup;
  currentTimestamp: Dayjs;
  visibleStart: Dayjs;
  visibleEnd: Dayjs;
  showTimeMarker: boolean;
  isLast: boolean;
}

const filterCandidatesByArea = (
  candidateEvents: EventCreate[],
  area: string
): EventCreate[] => {
  return candidateEvents.filter((candidate) => {
    const { area: candidateArea } = getCandidateLabels(candidate);
    return candidateArea === area;
  });
};

const filterEventsByArea = (
  events: ScrapEyesEvent[],
  area: string
): ScrapEyesEvent[] => {
  return events.filter((event) => event.area === area);
};

const buildScaleWeightTrace = ({
  seriesData,
  scrapCar,
  areaIndex,
  t,
}: {
  seriesData: ScaleWeightSeriesData;
  scrapCar: string;
  areaIndex: number;
  t: (key: string) => string;
}): Partial<Data> => {
  return {
    x: seriesData.x.map(toPlotlyUtcString),
    y: seriesData.y,
    type: "scatter" as const,
    mode: "lines" as const,
    name: t(scrapCar),
    line: {
      color: getAreaColor(areaIndex),
      width: 1.5,
    },
    hovertemplate: `<b>%{fullData.name}</b><br>Time (UTC): %{x}<br>Weight: %{y:.2f} t<extra></extra>`,
  };
};

const buildCandidateHoverTraces = ({
  candidates,
  t,
}: {
  candidates: EventCreate[];
  t: (key: string) => string;
}): Partial<Data>[] => {
  return candidates.map((candidate, index) => {
    const { mass } = getCandidateLabels(candidate);
    const startMs = new Date(candidate.event.start).getTime();
    const endMs = new Date(candidate.event.end).getTime();
    const midUtcString = toPlotlyUtcString(new Date((startMs + endMs) / 2));
    const hoverText = mass ?? "";
    return {
      x: [midUtcString],
      y: [0.5],
      yaxis: "y2",
      type: "scatter" as const,
      mode: "markers" as const,
      marker: {
        size: 40,
        opacity: 0,
      },
      hoverinfo: "text" as const,
      hovertemplate: `<b>${t(
        "candidateDropDetected"
      )}</b><br>${hoverText}<extra></extra>`,
      showlegend: false,
      customdata: [index],
    };
  });
};

const buildNearestCandidateAnnotation = ({
  nearestCandidate,
  t,
}: {
  nearestCandidate: EventCreate | null;
  t: (key: string) => string;
}): Partial<Annotations> | null => {
  if (!nearestCandidate) return null;
  const { mass } = getCandidateLabels(nearestCandidate);
  const labelText = mass ?? "";

  return {
    xref: "paper",
    x: 0.5,
    y: 1,
    yref: "paper",
    text: `<b>${t("candidateDropDetected")}</b>: ${labelText}`,
    showarrow: false,
    font: {
      size: 11,
      color: "#555",
    },
    bgcolor: "rgba(120, 120, 120, 0.15)",
    bordercolor: "rgba(100, 100, 100, 0.4)",
    borderwidth: 1,
    borderpad: 4,
    yanchor: "bottom",
    xanchor: "left",
  };
};

const buildCandidateShapes = ({
  candidates,
  nearestCandidate,
  seriesData,
  lookup,
}: {
  candidates: EventCreate[];
  nearestCandidate: EventCreate | null;
  seriesData: ScaleWeightSeriesData | undefined;
  lookup: ScaleReadingLookup;
}): Partial<Shape>[] => {
  return candidates.map((candidate) => {
    const isNearest =
      candidate.event.start === nearestCandidate?.event.start &&
      candidate.event.end === nearestCandidate.event.end;

    const { massValue } = getCandidateLabels(candidate);
    const startTimestamp = dayjs(candidate.event.start);
    const weightAtStart = findWeightAt(seriesData, startTimestamp, lookup);

    // Use data coordinates if we have the weight, otherwise fall back to paper
    const shouldUseDataCoords = weightAtStart !== null && massValue !== null;

    return {
      type: "rect",
      x0: toPlotlyUtcString(new Date(candidate.event.start)),
      x1: toPlotlyUtcString(new Date(candidate.event.end)),
      yref: shouldUseDataCoords ? "y" : "paper",
      y0: shouldUseDataCoords ? weightAtStart : 0,
      y1: shouldUseDataCoords ? weightAtStart + massValue : 1,
      fillcolor: isNearest
        ? "rgba(120, 120, 120, 0.2)"
        : "rgba(120, 120, 120, 0.08)",
      line: {
        color: isNearest
          ? "rgba(100, 100, 100, 0.6)"
          : "rgba(140, 140, 140, 0.3)",
        width: isNearest ? 2 : 1,
        dash: isNearest ? "solid" : "dot",
      },
    };
  });
};

const buildEventShapes = ({
  events,
  seriesData,
  lookup,
}: {
  events: ScrapEyesEvent[];
  seriesData: ScaleWeightSeriesData | undefined;
  lookup: ScaleReadingLookup;
}): Partial<Shape>[] => {
  return events.map((event) => {
    const color = eventTypeColor(event.eventType);
    const massValue = parseFiniteFloat(event.mass);
    const weightAtStart = findWeightAt(seriesData, event.start, lookup);

    const shouldUseDataCoords = weightAtStart !== null && massValue !== null;

    return {
      type: "rect",
      x0: toPlotlyUtcString(event.start.toDate()),
      x1: toPlotlyUtcString(event.end.toDate()),
      yref: shouldUseDataCoords ? "y" : "paper",
      y0: shouldUseDataCoords ? weightAtStart : 0,
      y1: shouldUseDataCoords ? weightAtStart + massValue : 1,
      fillcolor: `${color}40`,
      line: {
        color,
        width: 2,
      },
    };
  });
};

const buildLayout = ({
  visibleStart,
  visibleEnd,
  visibleYRange,
  currentTimeMarker,
  candidateShapes,
  eventShapes,
  nearestCandidateAnnotation,
  isLast,
}: {
  visibleStart: Dayjs;
  visibleEnd: Dayjs;
  visibleYRange: [number, number] | undefined;
  currentTimeMarker: Partial<Shape> | null;
  candidateShapes: Partial<Shape>[];
  eventShapes: Partial<Shape>[];
  nearestCandidateAnnotation: Partial<Annotations> | null;
  isLast: boolean;
}): Partial<Layout> => {
  return {
    autosize: true,
    height: 80,
    margin: { l: 35, r: 0, t: 20, b: isLast ? 20 : 0 },
    xaxis: {
      type: "date",
      range: [
        toPlotlyUtcString(visibleStart.toDate()),
        toPlotlyUtcString(visibleEnd.toDate()),
      ],
      showgrid: true,
      gridcolor: "#e0e0e0",
      showticklabels: isLast,
      tickformat: "%H:%M:%S",
      hoverformat: "%H:%M:%S",
      tickfont: { size: 9, color: "#888" },
    },
    yaxis: {
      showticklabels: true,
      showgrid: true,
      gridcolor: "#e0e0e0",
      fixedrange: true,
      range: visibleYRange,
      tickfont: { size: 9, color: "#888" },
      tickmode: "linear" as const,
      dtick: computeLinearDtick(visibleYRange),
    },
    yaxis2: {
      overlaying: "y",
      showticklabels: false,
      showgrid: false,
      range: [0, 1],
      fixedrange: true,
    },
    showlegend: false,
    hovermode: "closest",
    dragmode: "pan",
    shapes: [
      ...candidateShapes,
      ...eventShapes,
      ...(currentTimeMarker ? [currentTimeMarker] : []),
    ],
    annotations: nearestCandidateAnnotation ? [nearestCandidateAnnotation] : [],
  };
};

const buildPlotData = ({
  scaleWeightTrace,
  candidateHoverTraces,
}: {
  scaleWeightTrace: Partial<Data> | null;
  candidateHoverTraces: Partial<Data>[];
}): Partial<Data>[] => {
  const traces: Partial<Data>[] = [];
  if (scaleWeightTrace) traces.push(scaleWeightTrace);
  traces.push(...candidateHoverTraces);
  return traces;
};

export const ScaleWeightsSingleAreaChart = ({
  scrapCar,
  areaIndex,
  seriesData,
  events,
  candidateEvents,
  massAreaMatching,
  scaleReadingLookup,
  currentTimestamp,
  visibleStart,
  visibleEnd,
  showTimeMarker,
  isLast,
}: ScaleWeightsSingleAreaChartProps) => {
  const { t } = useTenantTranslation();
  const windowedSeries = useWindowedSeries({
    seriesData,
    visibleStart,
    visibleEnd,
  });

  const filteredCandidates = useMemo(
    () => filterCandidatesByArea(candidateEvents, scrapCar),
    [candidateEvents, scrapCar]
  );

  const filteredEvents = useMemo(
    () =>
      massAreaMatching !== false
        ? filterEventsByArea(events, scrapCar)
        : events,
    [events, scrapCar, massAreaMatching]
  );

  const nearestCandidate = useMemo(() => {
    if (filteredCandidates.length === 0) return null;
    return findNearestCandidate(filteredCandidates, currentTimestamp);
  }, [filteredCandidates, currentTimestamp]);

  const visibleYRange = useMemo(
    () =>
      computeVisibleYRange({
        seriesData: windowedSeries,
        visibleStart,
        visibleEnd,
        nearestCandidate,
        lookup: scaleReadingLookup,
      }),
    [
      windowedSeries,
      visibleStart,
      visibleEnd,
      nearestCandidate,
      scaleReadingLookup,
    ]
  );

  const scaleWeightTrace = useMemo(() => {
    if (!windowedSeries) return null;
    return buildScaleWeightTrace({
      seriesData: windowedSeries,
      scrapCar,
      areaIndex,
      t,
    });
  }, [windowedSeries, scrapCar, areaIndex, t]);

  const candidateHoverTraces = useMemo(
    () => buildCandidateHoverTraces({ candidates: filteredCandidates, t }),
    [filteredCandidates, t]
  );

  const plotData = useMemo(
    () => buildPlotData({ scaleWeightTrace, candidateHoverTraces }),
    [scaleWeightTrace, candidateHoverTraces]
  );

  const currentTimeMarker = showTimeMarker
    ? CENTRED_PLAYHEAD_MARKER_SHAPE
    : null;

  const nearestCandidateAnnotation = useMemo(
    () => buildNearestCandidateAnnotation({ nearestCandidate, t }),
    [nearestCandidate, t]
  );

  const candidateShapes = useMemo(
    () =>
      buildCandidateShapes({
        candidates: filteredCandidates,
        lookup: scaleReadingLookup,
        nearestCandidate,
        seriesData: windowedSeries,
      }),
    [filteredCandidates, nearestCandidate, windowedSeries, scaleReadingLookup]
  );

  const eventShapes = useMemo(
    () =>
      buildEventShapes({
        events: filteredEvents,
        seriesData: windowedSeries,
        lookup: scaleReadingLookup,
      }),
    [filteredEvents, windowedSeries, scaleReadingLookup]
  );

  const layout = useMemo(
    () =>
      buildLayout({
        visibleStart,
        visibleEnd,
        visibleYRange,
        currentTimeMarker,
        candidateShapes,
        eventShapes,
        nearestCandidateAnnotation,
        isLast,
      }),
    [
      visibleStart,
      visibleEnd,
      visibleYRange,
      currentTimeMarker,
      candidateShapes,
      eventShapes,
      nearestCandidateAnnotation,
      isLast,
    ]
  );

  return (
    <Box sx={{ position: "relative", overflow: "visible", width: "100%" }}>
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          top: 2,
          left: 8,
          zIndex: 1,
          color: getAreaColor(areaIndex),
          fontWeight: 600,
          fontSize: "0.7rem",
          lineHeight: 1,
        }}
      >
        {t(scrapCar)}
      </Typography>
      <Plot
        data={plotData}
        layout={layout}
        config={{
          displayModeBar: false,
          responsive: true,
          scrollZoom: false,
        }}
        style={{ width: "100%", overflow: "visible" }}
        useResizeHandler
      />
    </Box>
  );
};
